'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { useT, useApiLang } from '@/lib/useT';
import toast from 'react-hot-toast';
import { Mail, Lock, User, Building2, Globe, ArrowRight } from 'lucide-react';
import { Button, Input, Select } from '@/components/ui';
import { AuthShell, AuthCard, AuthField, AuthLinks, AuthLink } from '@/components/shell/AuthShell';

const COUNTRIES = [
  { code: 'DE', label: 'Deutschland', flag: '🇩🇪' },
  { code: 'AT', label: 'Österreich', flag: '🇦🇹' },
  { code: 'CH', label: 'Schweiz', flag: '🇨🇭' },
  { code: 'PL', label: 'Polska', flag: '🇵🇱' },
  { code: 'CZ', label: 'Česko', flag: '🇨🇿' },
  { code: 'UA', label: 'Україна', flag: '🇺🇦' },
  { code: 'RU', label: 'Россия', flag: '🇷🇺' },
  { code: 'MD', label: 'Moldova', flag: '🇲🇩' },
  { code: 'BG', label: 'България', flag: '🇧🇬' },
  { code: 'KZ', label: 'Қазақстан', flag: '🇰🇿' },
];

export default function RegisterPage() {
  const t = useT();
  const apiLang = useApiLang();
  const [form, setForm] = useState({
    name: '',
    surname: '',
    email: '',
    password: '',
    companyName: '',
    country: 'DE',
    language: 'DE',
    termsAccepted: false,
    privacyAccepted: false,
    marketingAccepted: false,
  });
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { setAuth } = useAuthStore();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error(t('reg_pw_too_short'));
      return;
    }
    setLoading(true);
    try {
      const data = await api.post('/auth/register', { ...form, language: apiLang });
      setAuth(data.accessToken, data.refreshToken, data.user);
      toast.success('Willkommen! Ihr Unternehmen wurde registriert.');
      router.push('/dashboard/onboarding');
    } catch (err: any) {
      toast.error(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const label = (key: string, fallback: string) => {
    const v = t(key);
    return !v || v === key ? fallback : v;
  };

  return (
    <AuthShell
      kicker={label('reg_kicker', 'Register your company')}
      headline={label('reg_headline', 'Set up your transport company in a few minutes.')}
      lede={label('reg_lede', 'Add drivers and vehicles, and start dispatching the same day. No installation, no setup fee.')}
      points={[
        label('reg_point_1', 'Free while you try it — no card required'),
        label('reg_point_2', 'Import your existing orders and fleet from Excel'),
        label('reg_point_3', 'Drivers get the mobile app and Telegram bot'),
      ]}
    >
      <AuthCard
        title={label('reg_title', 'Create your account')}
        subtitle={label('reg_subtitle', 'You become the owner of the organisation.')}
        footer={
          <span>
            {label('reg_already', 'Already have an account?')}{' '}
            <AuthLink href="/login">{label('reg_signin', 'Sign in')}</AuthLink>
          </span>
        }
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <AuthField label={label('reg_company', 'Company name')} icon={Building2}>
            <Input name="companyName" value={form.companyName} onChange={handleChange}
                   required placeholder={label('reg_company_ph', 'Muster Spedition GmbH')} />
          </AuthField>

          <div className="grid grid-cols-2 gap-3">
            <AuthField label={label('reg_first_name', 'First name')} icon={User}>
              <Input name="name" value={form.name} onChange={handleChange} required placeholder="Max" autoComplete="given-name" />
            </AuthField>
            <AuthField label={label('reg_last_name', 'Last name')}>
              <Input name="surname" value={form.surname} onChange={handleChange} required placeholder="Mustermann" autoComplete="family-name" />
            </AuthField>
          </div>

          <AuthField label={label('login_email', 'Email address')} icon={Mail}>
            <Input type="email" name="email" value={form.email} onChange={handleChange}
                   required autoComplete="email" placeholder="max@spedition.de" />
          </AuthField>

          <AuthField label={label('login_password', 'Password')} icon={Lock}
                     hint={label('reg_min_chars', 'At least 8 characters')}>
            <Input type="password" name="password" value={form.password} onChange={handleChange}
                   required minLength={8} autoComplete="new-password" placeholder="••••••••" />
          </AuthField>

          <AuthField label={label('reg_country', 'Country')} icon={Globe}>
            <Select name="country" value={form.country} onChange={handleChange}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.label}</option>)}
            </Select>
          </AuthField>

          {/* Согласия: обязательное отдельно от маркетингового — так требует DSGVO */}
          <div className="flex flex-col gap-2.5 pt-1">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.termsAccepted && form.privacyAccepted}
                onChange={(e) => setForm((prev) => ({ ...prev, termsAccepted: e.target.checked, privacyAccepted: e.target.checked }))}
                className="mt-0.5 w-4 h-4 accent-[var(--brand)] shrink-0"
              />
              <span className="text-[12px] leading-relaxed text-[var(--text-secondary)]">
                {t('consent_terms_prefix')}{' '}
                <a href="https://logixa.one/agb" target="_blank" rel="noopener noreferrer" className="text-[var(--brand)] underline">{t('consent_terms_link')}</a>
                {' '}{t('consent_and')}{' '}
                <a href="https://logixa.one/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[var(--brand)] underline">{t('consent_privacy_link')}</a>
                {' '}{t('consent_terms_suffix')}
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.marketingAccepted}
                onChange={(e) => setForm((prev) => ({ ...prev, marketingAccepted: e.target.checked }))}
                className="mt-0.5 w-4 h-4 accent-[var(--brand)] shrink-0"
              />
              <span className="text-[12px] leading-relaxed text-[var(--text-muted)]">{t('consent_marketing')}</span>
            </label>
          </div>

          <Button type="submit" variant="primary" disabled={loading || !form.termsAccepted}
                  className="w-full h-11 text-[14px]" icon={loading ? undefined : ArrowRight}>
            {loading ? label('reg_loading', 'Creating account…') : label('reg_submit', 'Create account')}
          </Button>
        </form>
      </AuthCard>

      <AuthLinks>
        <span>
          {label('reg_driver_link', 'Driving for a company?')}{' '}
          <AuthLink href="/register/driver">{label('reg_driver_link_here', 'Register as a driver')}</AuthLink>
        </span>
      </AuthLinks>
    </AuthShell>
  );
}
